<script lang="ts">
	
	import type { ChoiceGroup, FoodMenuChoiceIssueReport } from "$lib/def/food-menu";
	import type { getRestaurantState, Menu } from "$lib/serv/restaurant";
	import type { CartItem, MenuItem as IMenuItem, SelectionValue } from "$lib/ui/home/food/menu/server";

  import { setItem } from "$lib/serv/core/local-store";
  import { cart$, getQuote } from '$lib/serv/food-cart';
  import { gatherRestaurantCache, KEY_FOOD_ITEM, KEY_SEARCH, lookupRestaurant } from "$lib/serv/food-landing";
  import { toastError } from "$lib/serv/toast";
  import { getCartItem, getMenuItem, updateCart } from "$lib/ui/home/food/menu/server";
  import { hasGeo } from "$lib/util/address";
  import { extractErrorMessage, merchantNameSuburbSlug } from "$lib/util/format";
  import { truthy } from "$lib/util/func";
  import { RxWritable } from "$lib/util/obs";
  import { query } from "$lib/util/url";
  import { firstValueFrom, Subject, type ObservedValueOf } from "rxjs";
  import { onDestroy } from "svelte";
	
	import MenuItem from '$lib/ui/home/food/menu/RestaurantMenuItem.svelte';
	import ToolTip, { ToolPosition } from "$lib/ui/shared/ToolTip.svelte";
	import AddressSelect from "../layout/AddressSelect.svelte";
	import LoadingIcons from "../shared/LoadingIcons.svelte";
	import Modal from "../shared/Modal.svelte";
	import Quantity from "../shared/Quantity.svelte";

  //###########################################################################################
  // This component hosts the RestaurantMenuItem component in a modal. It also adds a quantity
	// selector and an add to cart button.
	// 
	// It will be responsable for (WIP):
	// * Load the menu (and be first to get restaurant id) and provide it to RestaurantMenuItem
	// * Gather input value for RestaurantMenuItem
	// * Digest selection events from RestaurantMenuItem
	// * Get quotes
	// * Communicating validation erros back to RestaurantMenuItem
	// * Updating the cart
  //###########################################################################################

	export let menu: Menu | undefined;

  /**
   * Should always have the following format:
	 * `[slug or id]:[optional menu section id]:[menu id]:[optional restaurant id]`
   */
	export let menuItemHash: string | null;

  /**
   * Cart item index being edited. The menu item being presented will repplace the cart item at this index.
   */
  export let editIndex: number | undefined = undefined;

	export let currentUrl: URL;

  /**
   * If false, then update/add-to-cart will navigate to the restaurant page.
   */
  export let hostedByRestaurantPage = true;

  $: hasLocation = hasGeo(Object.fromEntries(currentUrl.searchParams.entries()));

  let initItem: Awaited<ReturnType<typeof createInitItemPromise>> | undefined;
  $: menu && createInitItemPromise(menu, menuItemHash ?? undefined).then((p) => initItem = p);

  $: hrefClose = query(currentUrl).remove(KEY_FOOD_ITEM, 'edit').toUrl();

  const tArr = () => [] as ChoiceGroup<object>[];
  const selection$ = new RxWritable({
    variant_id: -1,
    options: tArr(),
    extras: tArr(),
    addons: tArr(),
  });
  const quantity$ = new RxWritable(1);

  let menuBusy = true;
  let updatingPrice = true;
  let restaurantId: number;
  let modalClose: () => void;

  async function createInitItemPromise(menu:Menu, menuItemHash?:string) {
    const mI = await getMenuItem(menu, menuItemHash);
    const cI = mI && getCartItem(mI, $cart$, editIndex);
    if (mI) {
      getSemistaticIfAvailable(mI.restaurant_id); // just for the post-add-to-cart redirect
      getDynamicIfAvailable(mI.restaurant_id); // just for the post-add-to-cart redirect
      if (mI.menu_deal_action === "buy_1_get_1_free") {
        // let's make sure the quantity is acceptable (in case of a default value)
        if (cI && cI.quantity % 2 !== 0) cI.quantity = 2;
      }
      restaurantId = mI.restaurant_id;
    }
    if (cI) quantity$.next(cI.quantity);
    if (cI) selection$.set({
      variant_id: cI.variant_id,
      options: cI.options ?? [],
      extras: cI.extras ?? [],
      addons: cI.addons ?? [],
    });
    return {
      loading: false,
      menuItem: mI,
      cartItem: cI
    }
  }

  async function promiseQuote({menuItem, cartItem}: Awaited<ReturnType<typeof createInitItemPromise>>, selection: ObservedValueOf<typeof selection$>, quantity: number) {
    if (!menuItem) return null;
    updatingPrice = true;
    let quote: ObservedValueOf<ReturnType<typeof getQuote>> | undefined;
    try {
      quote = await firstValueFrom(getQuote({
      restaurant: { id: menuItem.restaurant_id },
      menu_id: menuItem.menu_id,
      items: [{ ...cartItem, ...selection, quantity }]
    }));
    } catch (error) {
      toastError(extractErrorMessage(error, 'Failed to get a quote.'), true);
    }
    menuBusy = false;
    updatingPrice = false;
    return quote?.items[0] as any;
  }

  let restNameSuburb = '-';
  async function getSemistaticIfAvailable(id?: number) {
    if (!id) return;
    const rest = await lookupRestaurant(id, 'semi_static');
    if (!rest) return;
    restNameSuburb = merchantNameSuburbSlug(rest.name, rest.address?.suburb);
  }

  let vertical = 'restaurant';
  async function getDynamicIfAvailable(id?: number) {
    if (!id) return;
    const rest = await lookupRestaurant(id, 'dynamic');
    if (!rest) return;
    vertical = rest.vertical ?? 'restaurant';
  }

  function cartUpdated(restaurantId: number) {
    setItem("showCart", true);
    if ((hostedByRestaurantPage || !restaurantId) && modalClose) {
      modalClose(); // modal's hrefClose
    } else {
      const q = query(currentUrl).remove(KEY_FOOD_ITEM, KEY_SEARCH, 'edit');
      q.go(`/delivery/${vertical}/${restNameSuburb}/${restaurantId}`, { noScroll: true });
    }
  }
  
  interface Selection {
    key: string;
    value: number | SelectionValue;
  }

  function onSelection({key,value}: Selection) {
    menuBusy = true;
    selection$.update(s => ({ ...s, [key]: value }));
  }

  function setQty(q: number) {
    menuBusy = true;
    $quantity$ = q;
  }
  
  async function addToCart(menuItem: IMenuItem | undefined, cartItem: CartItem) {
    if (!menuItem) return;
    
    menuBusy = true;
    await updateCart(menuItem, cartItem, reporter, () => cartUpdated(restaurantId));
    menuBusy = false;
  }

	const reporter = new Subject<FoodMenuChoiceIssueReport>();
	const subReporter = reporter.subscribe(({report, highlight}) => {
		report('variant'); // no issues to report
	});

	let stateId: number | undefined;
	let promiseState: Promise<ReturnType<typeof getRestaurantState> | null> | undefined;
	function getRestState(restaurantId?: number) {
		if (!restaurantId) return null;
		if (stateId === restaurantId) return promiseState;
		stateId = restaurantId;
		promiseState = gatherRestaurantCache(restaurantId);
		return promiseState;
	}

  function itemOrSelectedVariantIsSoldOut({ menuItem, cartItem }: { menuItem: IMenuItem, cartItem: CartItem }) {
    const itemSoldOut = menuItem.availability && menuItem.availability?.status !== 'visible';
    const variantSoldOut = menuItem.variants.find((v) => v.id === cartItem.variant_id)?.availability?.status === 'sold_out';
    return itemSoldOut || variantSoldOut;
  }

	onDestroy(() => {
		subReporter.unsubscribe();
    menuBusy = false;
	});
</script>

{#if !hasLocation}
  <Modal hrefClose={hrefClose} bind:close={modalClose}>
    <AddressSelect currentURL={currentUrl} onlyParams={true} />
  </Modal>
{:else}
  {@const selection = $selection$ ?? null}
  {@const quantity = $quantity$}
  <Modal size="large" {hrefClose} bind:close={modalClose}>
    {#if initItem}
      {@const promisedQuote = promiseQuote(initItem, selection, quantity)}
      {#if initItem?.loading}
        <LoadingIcons />
      {/if}

      {#if initItem?.menuItem && initItem?.cartItem}
        <MenuItem
          menuItem={initItem.menuItem}
          cartItem={initItem.cartItem}
          pricePromise={promisedQuote}
          reporter={reporter}
          on:back={() => modalClose?.()}
          on:selection={(e) => onSelection(e.detail)}
        />
      {/if}
    {/if}

    <footer slot="footer" class="flex justify-end small-gap text-right">
      {#if initItem}
        {@const { menuItem, cartItem:initCartItem } = initItem}
        {@const cartItem = { ...initCartItem, ...selection, quantity} as CartItem}
        {#if menuItem && initCartItem}
          {@const statePromise = getRestState(restaurantId)}

          {#await statePromise}
            <LoadingIcons />
          {:then state}
            {#if !state || !state.offline}
              <Quantity
                quantity={cartItem?.quantity}
                step={menuItem?.menu_deal_action === "buy_1_get_1_free" ? 2 : 1}
                min={menuItem?.menu_deal_action === "buy_1_get_1_free" ? 2 : 1}
                max={cartItem?.maxQuantity}
                on:update={(e) => setQty(e.detail)}
              />
              <button
                class="button relative" class:loading={menuBusy}
                on:click={() => addToCart(menuItem, cartItem)}
                data-testid={`btnAddToCart`}
                disabled={menuBusy || !menuItem || itemOrSelectedVariantIsSoldOut({ menuItem, cartItem})}>
                {truthy(cartItem.index) ? 'Update cart' : 'Add to cart'}
                {#if !menuItem || itemOrSelectedVariantIsSoldOut({ menuItem, cartItem})}
                  <ToolTip message='Item sold out.' pos={ToolPosition.TC}/>
                {/if}
              </button>
            {:else}
              <div class="text-right">
                <span class="icon-clock-20" style="margin-right: .3rem;"></span>
                <span class="bold" style="margin-right: .3rem;">Offline {#if state.text}:{/if}</span>
                {#if state.text}<span>{state.text}</span>{/if}
              </div>
            {/if}
          {/await}
        {/if}
      {/if}
    </footer>
  </Modal>
{/if}

